import React, { useEffect, useState } from 'react';
import axiosInstance from '../../../interceptor/axiosInstance';
import Swal from 'sweetalert2';
import { AsyncPaginate } from 'react-select-async-paginate';
import ReactDOM from 'react-dom';
import './EditProgram.css';

const EditProgram = ({ programData, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: '',
    total_semesters: '',
    department: null,
  });
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [loading, setLoading] = useState(false);

  // Set initial form values
  useEffect(() => {
    if (programData) {
      setFormData({
        name: programData.name || '',
        total_semesters: programData.total_semesters || '',
        department: programData.department?.id || null,
      });

      if (programData.department) {
        setSelectedDepartment({
          value: programData.department.id,
          label: programData.department.name,
        });
      }
    }
  }, [programData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleDepartmentChange = (selectedOption) => {
    setSelectedDepartment(selectedOption);
    setFormData((prev) => ({
      ...prev,
      department: selectedOption?.value || null,
    }));
  };

  const loadDepartments = async (searchQuery, loadedOptions, { page }) => {
    try {
      const res = await axiosInstance.get(`programs/departments/?search=${searchQuery}&page=${page}`);
      const departments = res.data?.data?.departments || [];

      return {
        options: departments.map((dept) => ({
          value: dept.id,
          label: dept.name,
        })),
        hasMore: !!res.data?.data?.pagination?.next,
        additional: {
          page: page + 1,
        },
      };
    } catch (err) {
      return {
        options: [],
        hasMore: false,
        additional: {
          page: page,
        },
      };
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axiosInstance.put(`programs/programs/${programData.id}/`, formData);
      Swal.fire({
        title: 'Success!',
        text: 'Program updated successfully.',
        icon: 'success',
        timer: 2000,
        showConfirmButton: false,
      });
      onSuccess();
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to update program.';
      Swal.fire({
        title: 'Error!',
        text: errorMessage,
        icon: 'error',
        confirmButtonText: 'Ok',
      });
    } finally {
      setLoading(false);
    }
  };

  const modalContent = (
    <div className="edit-program-modal-overlay" onClick={onClose}>
      <div className="edit-program-modal-content" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="edit-program-modal-header">
          <div>
            <h2 className="edit-program-modal-title">Edit Program</h2>
            <p className="edit-program-modal-subtitle">Update program information</p>
          </div>
          <button className="edit-program-modal-close" onClick={onClose}>
            ✕
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleUpdate} className="edit-program-modal-form">
          {/* Program Name */}
          <div className="edit-program-modal-field">
            <label className="edit-program-modal-label">
              Program Name <span className="edit-program-modal-required">*</span>
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className="edit-program-modal-input"
              placeholder="Enter program name..."
              required
            />
          </div>

          {/* Total Semesters */}
          <div className="edit-program-modal-field">
            <label className="edit-program-modal-label">
              Total Semesters <span className="edit-program-modal-required">*</span>
            </label>
            <input
              type="number"
              name="total_semesters"
              value={formData.total_semesters}
              onChange={handleChange}
              className="edit-program-modal-input"
              placeholder="Enter number of semesters..."
              min="1"
              required
            />
          </div>

          {/* Department */}
          <div className="edit-program-modal-field">
            <label className="edit-program-modal-label">
              Department <span className="edit-program-modal-required">*</span>
            </label>
            <AsyncPaginate
              value={selectedDepartment}
              loadOptions={loadDepartments}
              onChange={handleDepartmentChange}
              additional={{ page: 1 }}
              isSearchable
              placeholder="Search and select department..."
              className="edit-program-modal-select"
              classNamePrefix="edit-program-select"
              menuPortalTarget={document.body}
              styles={{
                menuPortal: base => ({
                  ...base,
                  zIndex: 100002,
                }),
              }}
            />
          </div>

          {/* Action Buttons */}
          <div className="edit-program-modal-actions">
            <button
              type="button"
              onClick={onClose}
              className="edit-program-modal-btn-cancel"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="edit-program-modal-btn-submit"
            >
              {loading ? (
                <>
                  <span className="edit-program-modal-spinner"></span>
                  Updating...
                </>
              ) : (
                <>
                  <span>✓</span>
                  Update Program
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};

export default EditProgram;
